import { useCallback, useEffect, useState } from "react";
import {
  fetchDispensaryActiveRuns,
  fetchOrgActiveRuns,
  type DispensaryActiveRunItem,
} from "../lib/dispensary-delivery-api";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

export function RunsActiveScreen() {
  const { dispensaryId, orgId, allLocations, getToken } = useDispensaryWorkspace();
  const aggregate = allLocations && orgId != null;
  const [runs, setRuns] = useState<DispensaryActiveRunItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!dispensaryId && !aggregate) return;
    setLoading(true);
    setError(null);
    try {
      const r = aggregate
        ? await fetchOrgActiveRuns(getToken, orgId!)
        : await fetchDispensaryActiveRuns(getToken, dispensaryId!);
      setRuns(r.runs);
    } catch {
      setError("Could not load active runs.");
    } finally {
      setLoading(false);
    }
  }, [dispensaryId, aggregate, orgId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!dispensaryId && !aggregate) {
    return <p className="text-sm text-amber-200/90">Set dispensary scope to view runs.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Active runs</h1>
          <p className="mt-1 text-sm text-gray-500">
            {aggregate
              ? "Runs currently in progress across all your locations."
              : "Runs currently in progress for this shop. Map and live vehicle pins are a planned follow-up."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:border-green-hover disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      {error ? <p className="text-sm text-red-300">{error}</p> : null}
      {runs.length === 0 && !loading ? (
        <p className="text-sm text-gray-500">
          {aggregate ? "No active runs across your locations." : "No active runs for this shop."}
        </p>
      ) : (
        <ul className="space-y-3">
          {runs.map((r) => (
            <li
              key={r.id}
              className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700"
            >
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-mono text-xs text-gray-500">{r.id.slice(0, 8)}…</span>
                <span className="font-mono text-green">{r.status}</span>
              </div>
              <p className="mt-2 text-gray-700">{r.driverFullName}</p>
              {r.metrcManifestNumber ? (
                <p className="mt-1 text-xs text-gray-500">Manifest #{r.metrcManifestNumber}</p>
              ) : null}
              <p className="mt-1 text-xs text-gray-600">Updated {r.updatedAt.slice(0, 19)}Z</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
